/**
 * Utility functions for SSQ title display
 */

import { Campaign } from "src/api/client-api/useCampaign";
import { formatDateCampaign } from "./campaigns";
import { SsqVersionSsq } from "src/api/sure-api/ssq/useGetOneSsqVersion.ts";

export interface SsqWithTitle {
    title: string;
    displayTitle?: string | null;
}

/**
 * Get the display title for an SSQ, with fallback to title
 * @param ssq - SSQ object with title and optional displayTitle
 * @returns The displayTitle if available and not empty, otherwise the title
 */
export const getSsqDisplayTitle = (ssq: SsqWithTitle | null | undefined): string => {
    if (!ssq) return "";

    // Use displayTitle if it exists and is not empty/null
    if (ssq.displayTitle && ssq.displayTitle.trim() !== "") {
        return ssq.displayTitle;
    }

    // Fallback to title
    return ssq.title || "";
};

/**
 * Get the option label for the SSQ selector (autocomplete).
 * Shows the technical `title` first, then the business `displayTitle` when it exists
 * and differs, so records sharing the same displayTitle can be told apart.
 * @param ssq - SSQ option with title and optional displayTitle
 * @returns "title - displayTitle", or just the title when displayTitle is empty/identical
 */
export const getSsqOptionName = (ssq: SsqWithTitle): string => {
    const title = ssq.title?.trim() ?? "";
    const displayTitle = ssq.displayTitle?.trim();

    if (!displayTitle || displayTitle === title) {
        return title;
    }

    return `${title} - ${displayTitle}`;
};

export const getSsqDisplayTitleWithCampaign = (ssq: SsqVersionSsq | undefined, campaign: Campaign | null): string => {
    if (!ssq) {
        return "";
    }

    if (!campaign) {
        return getSsqDisplayTitle(ssq);
    }

    return getSsqDisplayTitle(ssq)
        .concat(" - ")
        .concat(formatDateCampaign(campaign.startDate, campaign.endDate, "/"));
};
